import axios from 'axios'
import type { User } from '../types'
import * as Sentry from '@sentry/react'
import { socketConnect } from '../socket'

axios.defaults.withCredentials = true

// Maintenance state callback
let maintenanceCallback: ((state: boolean) => void) | null = null

export const setMaintenanceCallback = (callback: (state: boolean) => void) => {
	maintenanceCallback = callback
}

// notAllowed state callback
let notAllowedCallback: ((notAllowed: string | null) => void) | null = null

export const setNotAllowedCallback = (callback: (notAllowed: string | null) => void) => {
	notAllowedCallback = callback
}

axios.interceptors.response.use(
	response => {
		return response
	},
	err => {
		return new Promise((resolve, reject) => {
			if (err.response) {
				switch (err.response.status) {
					case 400:
						throw err
					case 401:
						redirectToArtemisID('Unauthorized')
						break
					case 403:
						if (notAllowedCallback) {
							notAllowedCallback(err.response.data.message ?? '403 Forbidden')
						}
						break
					case 404:
						if (notAllowedCallback) {
							notAllowedCallback(err.response.data.message ?? '404 Not Found')
						}
						break
					case 503:
						if (maintenanceCallback) {
							maintenanceCallback(true)
						}
						break
					default:
						Sentry.captureException(err)
						if (process.env.REACT_APP_SENTRY_ENV === 'live') {
							err.response.data.message = 'Server error'
						}
						throw err
				}
			}
		})
	}
)

export async function signIn(code: string): Promise<{ isOk: boolean; data?: User; message?: string }> {
	return new Promise((resolve, reject) => {
		axios
			.get(`${API_ID_URL}/auth/token/${code}`)
			.then(response => {
				setAccessToken()
				socketConnect()
				resolve(getUser())
			})
			.catch(error => {
				redirectToArtemisID('InvalidCode')
				reject({
					isOk: false,
				})
			})
	})
}

export function redirectToArtemisID(code: string) {
	clearAccessToken()
	const prostfix = window.location.hostname.includes('.us.') ? 'us' : ''
	window.location.href = `${ID_URL}/identifier/fiber${prostfix}?code=${code}`
}

export function setAccessToken() {
	localStorage.setItem('access_token', Date.now().toString())
}

export function hasAccessToken() {
	return localStorage.getItem('access_token') !== null
}

export function clearAccessToken() {
	localStorage.removeItem('access_token')
}

export async function getUser(): Promise<{
	isOk: boolean
	data?: User
	message?: string
}> {
	if (hasAccessToken()) {
		socketConnect()
		return new Promise((resolve, reject) => {
			axios
				.get(`${API_ID_URL}/auth/me?full=1&tracking=1`)
				.then(response => {
					const { user } = response.data
					if (!user.IsActive) {
						throw new Error('User is not active')
					}
					const apps = Object.values(user.applications)
					if (apps.indexOf('Legacy') > -1 || apps.indexOf('Fiber') > -1 || apps.indexOf('FiberUS') > -1) {
						Promise.all([
							axios.get(`${API_FIBER_URL}/users/${user.UserID}/field-permissions`),
							axios.get(`${API_FIBER_URL}/users/${user.UserID}/view-permissions`),
						]).then(([fieldPermissionsResponse, viewPermissionsResponse]) => {
							const fieldPermissions = fieldPermissionsResponse.data.success
								? fieldPermissionsResponse.data.data
								: []
							const viewPermissions = viewPermissionsResponse.data.success
								? viewPermissionsResponse.data.data
								: []
							const exportWhitelist = viewPermissionsResponse.data.export_whitelist ?? false

							resolve({
								isOk: true,
								data: {
									email: user.Username.split('@')[0],
									apps: apps,
									fieldPermissions: fieldPermissions,
									viewPermissions: viewPermissions,
									exportWhitelist: exportWhitelist,
								},
							})
							Sentry.setUser({
								id: user.UserID,
								email: user.Username,
							})
						})
					} else {
						redirectToArtemisID('NoAccess')
						resolve({
							isOk: false,
						})
					}
				})
				.catch(error => {
					redirectToArtemisID('ServerError')
					reject({
						isOk: false,
					})
				})
		})
	} else {
		return {
			isOk: false,
		}
	}
}
